import { useCallback, useEffect, useState } from 'react';
import { FilterableHistoryType } from 'src/@types/historyTables';
import useHistoryTableFilterStore from 'src/stores/historyTableFilterStore/useHistoryTableFilterStore';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { SHORT_TERM_API_PAGE_SIZE } from 'src/constants/historyTables';

interface UseHistoryPaginationResult {
	/** Current UI page (0-indexed) */
	currentUiPage: number;
	/** Current API page (0-indexed, for short-term data) */
	currentApiPage: number;
	/** Change to a specific UI page */
	setUiPage: (page: number) => void;
	/** Handle page change with optional custom page size */
	executePageChange: (newPage: number, uiPageSize?: number) => void;
}

/**
 * Hook for managing history table pagination state.
 * Handles the mapping between UI pages and API pages.
 * Resets page state when account changes or component unmounts.
 */
export const useHistoryPagination = (
	historyType: FilterableHistoryType
): UseHistoryPaginationResult => {
	const setHistoryTableFilterState = useHistoryTableFilterStore((s) => s.set);
	const currentUiPage = useHistoryTableFilterStore(
		(s) => s.getFilterState(historyType).currentUiPage
	);

	const [currentApiPage, setCurrentApiPage] = useState(0);

	const currentAccountPubKey = useDriftAccountStore((s) =>
		s.getCurrentUserAccount()?.pubKey?.toString()
	);

	const setUiPage = useCallback(
		(newPage: number) => {
			setHistoryTableFilterState((s) => {
				const filterState = s[historyType as keyof typeof s];
				if (filterState && 'currentUiPage' in filterState) {
					(filterState as { currentUiPage: number }).currentUiPage = newPage;
				}
			});
		},
		[historyType, setHistoryTableFilterState]
	);

	const executePageChange = useCallback(
		(uiPage: number, uiPageSize: number = SHORT_TERM_API_PAGE_SIZE) => {
			// Validate that uiPageSize is a divisor of API page size
			if (SHORT_TERM_API_PAGE_SIZE % uiPageSize !== 0) {
				throw new Error(
					`uiPageSize must be a perfect divisor of PAGE_SIZE(${SHORT_TERM_API_PAGE_SIZE})`
				);
			}

			// Convert UI page to API page
			const apiPage = Math.floor(
				(uiPage * uiPageSize) / SHORT_TERM_API_PAGE_SIZE
			);

			setUiPage(uiPage);
			setCurrentApiPage(apiPage);
		},
		[setUiPage]
	);

	// Reset pages when account changes
	useEffect(() => {
		setCurrentApiPage(0);
		setUiPage(0);
	}, [currentAccountPubKey, setUiPage]);

	// Reset pages on unmount
	useEffect(() => {
		return () => {
			setHistoryTableFilterState((s) => {
				const filterState = s[historyType as keyof typeof s];
				if (filterState && 'currentUiPage' in filterState) {
					(filterState as { currentUiPage: number }).currentUiPage = 0;
				}
			});
		};
	}, [historyType, setHistoryTableFilterState]);

	return {
		currentUiPage,
		currentApiPage,
		setUiPage,
		executePageChange,
	};
};
